import { useState, useEffect, useCallback, useMemo } from 'react';
import { mesTexto } from '../components/trabajos/ProximoMantenimiento';
import { trabajoHecho } from '../utils/pdf/pdfShared';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { generarRemitoPDFPremium } from '../utils/generadorPdfRemito';
import { resolverFechas } from '../utils/dateUtils';
import { POR_PAGINA } from '../utils/paginacion';
import { totalServicio } from '../utils/descuento';

/**
 * useServicioManager
 * Gestión de servicios técnicos con paginación y filtros server-side.
 */
export function useServicioManager() {
    const { usuario, esAdmin } = useAuth();

    // ── Lista ───────────────────────────────────────────────────────────────────
    const [servicios, setServicios]     = useState([]);
    const [cargando, setCargando]       = useState(true);
    const [pagina, setPagina]           = useState(0); // 0-based para Spring
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [totalItems, setTotalItems]   = useState(0);

    // ── Stats ────────────────────────────────────────────────────────────────────
    const [stats, setStats] = useState({
        totalMes: 0, cantidadMes: 0,
        totalHoy: 0, cantidadHoy: 0,
        gananciaTotal: 0,
        pendientesCount: 0, pendientesVal: 0,
    });

    // ── Filtros (interfaz compatible con FiltrosPanel) ──────────────────────────
    const [busquedaInput, setBusquedaInput]   = useState('');
    const [busquedaApi, setBusquedaApi]       = useState('');
    const [estado, setEstadoInternal]         = useState(esAdmin ? 'PENDIENTE_FACTURACION' : 'TODOS'); // técnico: lista única "Mis trabajos"
    const [periodoRapido, setPeriodoRapido]   = useState('MES');
    const [mesSelector, setMesSelector]       = useState('');
    const [desde, setDesde]                   = useState('');
    const [hasta, setHasta]                   = useState('');
    const [usuarioId, setUsuarioId]           = useState('');
    const [ordenServicio, setOrdenServicio]   = useState('fechaServicio,desc');

    // ── Modales ──────────────────────────────────────────────────────────────────
    const [modalCrear, setModalCrear]             = useState(false);
    const [servicioEditar, setServicioEditar]     = useState(null);
    const [modalDetalle, setModalDetalle]         = useState(null);
    const [modalFirmas, setModalFirmas]           = useState(false);
    const [pendingPdfServicio, setPendingPdfServicio] = useState(null);

    // Debounce de búsqueda — 500ms para no saturar la API
    useEffect(() => {
        const t = setTimeout(() => { setBusquedaApi(busquedaInput); setPagina(0); }, 500);
        return () => clearTimeout(t);
    }, [busquedaInput]);

    // ── Setters con reset de página ──────────────────────────────────────────────
    const setBusqueda   = (v) => setBusquedaInput(v);
    const setEstado     = (v) => { setEstadoInternal(v); setPagina(0); };
    const aplicarRapido = (tipo) => {
        setPeriodoRapido(tipo); setMesSelector('');
        setDesde(''); setHasta(''); setPagina(0);
    };
    const aplicarMesSelector = (val) => {
        setMesSelector(val); setPeriodoRapido('CUSTOM');
        setDesde(''); setHasta(''); setPagina(0);
    };
    const aplicarRango = (d, h) => {
        setDesde(d); setHasta(h);
        setPeriodoRapido('CUSTOM'); setMesSelector(''); setPagina(0);
    };

    // Filtros comunes a la lista Y a los contadores de las pestañas (período, técnico,
    // búsqueda). Antes los contadores ignoraban el período y contaban TODO el historial:
    // la pestaña decía "125" y la lista, filtrada al mes, mostraba 2.
    const paramsBase = useMemo(() => {
        const fechas = resolverFechas(periodoRapido, mesSelector, desde, hasta);
        const p = { tipo: 'TECNICA' };
        if (busquedaApi) p.busqueda = busquedaApi;
        else {
            if (fechas.desde) p.desde = fechas.desde;
            if (fechas.hasta) p.hasta = fechas.hasta;
        }
        if (!esAdmin && usuario?.id) p.usuarioId = usuario.id;
        else if (usuarioId)           p.usuarioId = usuarioId;
        return p;
    }, [busquedaApi, periodoRapido, mesSelector, desde, hasta, usuarioId, esAdmin, usuario?.id]);

    // ── Fetch lista ──────────────────────────────────────────────────────────────
    const cargarServicios = useCallback(async () => { // eslint-disable-line
        setCargando(true);
        try {
            const fechas = resolverFechas(periodoRapido, mesSelector, desde, hasta);
            const params = {
                tipo: 'TECNICA',
                page: pagina,
                size: POR_PAGINA,
                sort: ordenServicio,
            };
            // Tabs compuestos: mapear a estados reales del backend
            const estadoMap = {
                COBRADO:   'COBRADO,REALIZADO',
                PENDIENTE_FACTURACION: 'PENDIENTE_FACTURACION,COMPLETADO',
                HISTORIAL: 'COMPLETADO,CANCELADO,ARCHIVADO',
            };
            if (estado !== 'TODOS')  params.estado = estadoMap[estado] || estado;
            if (busquedaApi)         params.busqueda  = busquedaApi;
            // Si hay búsqueda activa, ignorar período para buscar en todo el historial
            if (!busquedaApi) {
                if (fechas.desde)    params.desde     = fechas.desde;
                if (fechas.hasta)    params.hasta     = fechas.hasta;
            }
            // técnico: siempre filtra por su propio ID; admin: usa el selector
            if (!esAdmin && usuario?.id) params.usuarioId = usuario.id;
            else if (usuarioId)           params.usuarioId = usuarioId;

            const res = await api.get('/servicios', { params });
            const page = res.data;
            setServicios(page.content || []);
            setTotalPaginas(page.totalPages || 1);
            setTotalItems(page.totalElements || 0);
        } catch {
            toast.error('Error al cargar servicios');
        } finally {
            setCargando(false);
        }
    }, [pagina, estado, busquedaApi, periodoRapido, mesSelector, desde, hasta, usuarioId, ordenServicio, esAdmin, usuario?.id]);

    // ── Fetch stats (separado de la lista) ───────────────────────────────────────
    const cargarStats = useCallback(async () => {
        try {
            const res = await api.get('/servicios/resumen', { params: { tipo: 'TECNICA' } });
            setStats(res.data);
        } catch { /* stats no críticos — silenciar */ }
    }, []);

    useEffect(() => { cargarServicios(); }, [cargarServicios]);
    useEffect(() => { cargarStats(); },   [cargarStats]);

    // ── Acciones ─────────────────────────────────────────────────────────────────
    const confirmarServicio = async (id, estadoDestino = 'COBRADO', { modalidadCobro, montoFinal, observaciones } = {}) => {
        const labels = {
            COBRADO: 'Cobrado', COMPLETADO: 'Completado',
            PENDIENTE_FACTURACION: 'Pendiente facturacion', FACTURADO: 'Facturado',
        };
        const loading = toast.loading('Procesando...');
        try {
            const payload = { estado: estadoDestino };
            if (modalidadCobro) payload.modalidadCobro = modalidadCobro;
            if (montoFinal != null) payload.montoFinal = montoFinal;
            if (observaciones != null) payload.observaciones = observaciones;
            await api.patch(`/servicios/${id}/estado`, payload);
            toast.success(labels[estadoDestino] || 'Actualizado', { id: loading });
            cargarServicios(); cargarStats();
            return true;
        } catch { toast.error('Error al actualizar', { id: loading }); return false; }
    };

    const eliminarServicio = async (id) => {
        try {
            await api.delete(`/servicios/${id}`);
            toast.success('Registro eliminado');
            cargarServicios(); cargarStats();
        } catch { toast.error('Error al eliminar'); }
    };

    // La confirmación ("¿archivar?") ahora la muestra el componente que llama
    // a esto, con el ConfirmDialog compartido — antes usaba window.confirm()
    // nativo del navegador, inconsistente con el resto de la app.
    const archivarServicio = async (id) => {
        const loading = toast.loading('Archivando...');
        try {
            await api.patch(`/servicios/${id}/estado`, { estado: 'ARCHIVADO' });
            toast.success('Servicio archivado', { id: loading });
            cargarServicios(); cargarStats();
        } catch { toast.error('Error al archivar', { id: loading }); }
    };

    // Idem archivarServicio: la confirmación se muestra afuera, con el
    // ConfirmDialog compartido, antes de llamar a esta función.
    const accionMasiva = async (ids, accion) => {
        const loading = toast.loading(`Procesando ${ids.length} servicios...`);
        try {
            await Promise.all(ids.map(id => api.patch(`/servicios/${id}/estado`, { estado: accion })));
            toast.success(`${ids.length} servicios actualizados`, { id: loading });
            cargarServicios(); cargarStats();
        } catch { toast.error('Error en la operación masiva', { id: loading }); }
    };

    // Presupuestos y archivados → PDF directo sin firmas; trabajo confirmado → modal de firmas
    const esPresupuesto = (s) => !trabajoHecho(s) && (s.estado || '').toUpperCase() !== 'CANCELADO';
    const sinFirmaDirecto = (s) => {
        const est = (s.estado || '').toUpperCase();
        return esPresupuesto(s) || est === 'ARCHIVADO';
    };

    const generarPDF = (servicio, { sinPrecios = false } = {}) => {
        if (sinFirmaDirecto(servicio)) {
            generarPDFDirecto(servicio, { firmaTecnico: null, firmaCliente: null, incluirFirmas: false, sinPrecios });
            return;
        }
        setPendingPdfServicio({ ...servicio, _sinPrecios: sinPrecios });
        setModalFirmas(true);
    };

    const confirmarFirmasYGenerarPDF = async ({ firmaTecnico, firmaCliente, aclaracionCliente = '', incluirFirmas = true }) => {
        setModalFirmas(false);
        const servicio = pendingPdfServicio;
        const sinPrecios = servicio?._sinPrecios || false;
        setPendingPdfServicio(null);
        if (!servicio) return;
        await generarPDFDirecto(servicio, { firmaTecnico, firmaCliente, aclaracionCliente, incluirFirmas, sinPrecios });
    };

    const generarPDFDirecto = async (servicio, { firmaTecnico, firmaCliente, aclaracionCliente = '', incluirFirmas = true, sinPrecios = false }) => {
        // Forzar sin firmas si es presupuesto (por seguridad, aunque el caller ya lo haga)
        const esPpto = esPresupuesto(servicio);
        if (esPpto) incluirFirmas = false;

        const loading = toast.loading('Preparando PDF...');
        try {
            const itemsConFotos = (servicio.items || []).map(it => ({
                ...it,
                totalCalculado:  parseFloat(it.costo)      || 0,
                costoExtra:      parseFloat(it.costoExtra) || 0,
                modeloEquipo:    it.modeloEquipo    || it.equipoModelo    || null,
                ubicacionEquipo: it.ubicacionEquipo || it.equipoUbicacion || null,
                equipoPiso:      it.equipoPiso      || null,
                equipoSector:    it.equipoSector    || null,
                trabajo:         it.trabajo         || it.trabajoRealizado || '',
                esVisita:        it.esVisita || it.trabajoTipo === 'VISITA' || false,
            }));
            // Para ventas presupuesto → tipo explícito para usar condiciones comerciales
            const esVenta = servicio.servicioTipo === 'VENTA';
            await generarRemitoPDFPremium({
                tipo:                 esVenta && esPpto ? 'PRESUPUESTO_VENTA' : undefined,
                esPresupuesto:        esPpto,
                servicioId:           servicio.id,
                nroDocumentoExistente: servicio.nroDocumento || localStorage.getItem(`pdf_nro_${servicio.id}`) || null,
                cliente: {
                    nombre:       servicio.clienteNombre,
                    telefono:     servicio.clienteTelefono,
                    email:        servicio.clienteEmail,
                    cuilDni:      servicio.clienteDni,
                    condicionIva: servicio.clienteCondicionIva,
                },
                sede: {
                    nombreSede: servicio.sedeNombre,
                    direccion:  servicio.sedeDireccion,
                    piso:       servicio.sedePiso,
                    depto:      servicio.sedeDepto,
                },
                modalidadCobro:      servicio.modalidadCobro || null,
                proximoMantenimiento: esPpto ? null : mesTexto(servicio.proximoMantenimiento),
                montoFinal:          servicio.montoFinal ?? null,
                tecnico:             itemsConFotos[0]?.tecnico || servicio.usuarioNombre || localStorage.getItem('tecnico_nombre') || 'Técnico',
                ticketItems:         itemsConFotos,
                fechaServicio:       servicio.fecha,
                descuentoPorcentaje: servicio.descuentoPorcentaje || 0,
                descuentoAlcance:    servicio.descuentoAlcance || 'TOTAL',
                totales:             servicio.totales || null,
                leyenda:             servicio.observaciones || '',
                esTecnicoForzado:    servicio.servicioTipo === 'TECNICA',
                fotosTrabajo:        esPpto ? [] : (servicio.fotosTrabajo || []),
                firmaTecnico:        firmaTecnico  || null,
                firmaCliente:        firmaCliente  || null,
                aclaracionCliente:   aclaracionCliente || '',
                incluirFirmas:       incluirFirmas,
                sinPrecios,
            });
            toast.success('PDF generado', { id: loading });
        } catch (e) {
            console.error('Error generando PDF:', e);
            toast.error('Error al generar PDF', { id: loading });
        }
    };

    // Total con descuento: el que calcula el backend (s.totales)
    const calcularTotal = (s) => totalServicio(s);

    const abrirEditar   = (servicio) => { setServicioEditar(servicio); setModalCrear(true); };
    const cerrarModal   = ()          => { setModalCrear(false); setServicioEditar(null); };

    // setFiltroTab: alias para compatibilidad con ServicioManager (click en card Pendientes)
    const setFiltroTab = (tab) => {
        if      (tab === 'PENDIENTES') setEstado('PRESUPUESTO');
        else if (tab === 'REALIZADOS') setEstado('COBRADO');
        else                           setEstado('TODOS');
    };

    // ── Objeto filtros compatible con FiltrosPanel ────────────────────────────────
    const filtros = {
        busqueda: busquedaInput, setBusqueda,
        estado, setEstado,
        periodoRapido, aplicarRapido,
        mesSelector, aplicarMesSelector,
        desde, hasta, aplicarRango,
        mesesDisponibles: [], // sin dropdown de meses en modo server-side
        paramsBase,
        totalItems,
        pagina: pagina + 1,       // FiltrosPanel y Paginacion usan 1-based
        totalPaginas,
        itemsPagina:    servicios,
        itemsFiltrados: servicios, // para exportarCSV
        irA:  (n) => setPagina(n - 1),
        next: ()  => setPagina((p) => Math.min(p + 1, totalPaginas - 1)),
        prev: ()  => setPagina((p) => Math.max(p - 1, 0)),
    };

    return {
        servicios,
        cargando, stats,
        modalCrear, setModalCrear,
        servicioEditar,
        modalDetalle, setModalDetalle,
        modalFirmas, setModalFirmas,
        confirmarFirmasYGenerarPDF,
        cargarServicios: () => { cargarServicios(); cargarStats(); },
        confirmarServicio,
        eliminarServicio, archivarServicio, accionMasiva, generarPDF,
        calcularTotal, abrirEditar, cerrarModal,
        setFiltroTab,
        filtros,
        usuarioId, setUsuarioId,
        ordenServicio, setOrdenServicio,
    };
}
